import { Command } from "cmdk";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/auth/AuthContext";
import { useTheme } from "@/components/Theme";

interface CommandItem {
  id: string;
  label: string;
  group: string;
  shortcut?: string;
  perform: () => void;
  adminOnly?: boolean;
}

/**
 * Global ⌘K palette. Always mounted; toggles open on `Cmd/Ctrl+K` and
 * any registered shortcut. Items are routes by default but anything
 * with a `perform` callback shows up too (theme cycle, sign out).
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { isSuperAdmin, logout } = useAuth();
  const { set: setTheme } = useTheme();

  const items: CommandItem[] = [
    // Navigation
    { id: "go-terminal", label: "Go to Terminal", group: "Navigation", shortcut: "G T", perform: () => navigate("/terminal") },
    { id: "go-portfolio", label: "Go to Portfolio", group: "Navigation", shortcut: "G P", perform: () => navigate("/portfolio") },
    { id: "go-orders", label: "Go to Orders", group: "Navigation", shortcut: "G O", perform: () => navigate("/orders") },
    { id: "go-fills", label: "Go to Fills", group: "Navigation", shortcut: "G F", perform: () => navigate("/fills") },
    { id: "go-alerts", label: "Go to Alerts", group: "Navigation", shortcut: "G A", perform: () => navigate("/alerts") },
    { id: "go-strategies", label: "Go to Strategies", group: "Navigation", perform: () => navigate("/strategies") },
    { id: "go-robots", label: "Go to Robots", group: "Navigation", perform: () => navigate("/robots") },
    { id: "go-analytics", label: "Go to Analytics", group: "Navigation", perform: () => navigate("/analytics") },
    { id: "go-trading-accounts", label: "Go to Trading accounts", group: "Navigation", perform: () => navigate("/trading-accounts") },
    { id: "go-broker", label: "Go to Broker accounts (legacy)", group: "Navigation", perform: () => navigate("/broker-accounts") },
    { id: "go-profile", label: "Go to Profile", group: "Navigation", perform: () => navigate("/profile") },

    // Admin nav
    { id: "go-admin-organizations", label: "Admin: Tenants", group: "Admin", adminOnly: true, perform: () => navigate("/admin/organizations") },
    { id: "go-admin-users", label: "Admin: Users", group: "Admin", adminOnly: true, perform: () => navigate("/admin/users") },
    { id: "go-admin-account-groups", label: "Admin: Account groups", group: "Admin", adminOnly: true, perform: () => navigate("/admin/account-groups") },
    { id: "go-admin-dealing-desk", label: "Admin: Dealing desk", group: "Admin", adminOnly: true, perform: () => navigate("/admin/dealing-desk") },
    { id: "go-admin-lp-sessions", label: "Admin: LP sessions", group: "Admin", adminOnly: true, perform: () => navigate("/admin/lp-sessions") },
    { id: "go-admin-risk-limits", label: "Admin: Risk limits", group: "Admin", adminOnly: true, perform: () => navigate("/admin/risk-limits") },
    { id: "go-admin-audit", label: "Admin: Audit log", group: "Admin", adminOnly: true, perform: () => navigate("/admin/audit") },
    { id: "go-admin-usage", label: "Admin: Usage & billing", group: "Admin", adminOnly: true, perform: () => navigate("/admin/usage") },
    { id: "go-admin-domains", label: "Admin: Custom domains", group: "Admin", adminOnly: true, perform: () => navigate("/admin/custom-domains") },
    { id: "go-admin-oidc", label: "Admin: OIDC / SSO", group: "Admin", adminOnly: true, perform: () => navigate("/admin/oidc") },
    { id: "go-admin-kyc", label: "Admin: KYC providers", group: "Admin", adminOnly: true, perform: () => navigate("/admin/kyc") },
    { id: "go-admin-branding", label: "Admin: Branding", group: "Admin", adminOnly: true, perform: () => navigate("/admin/branding") },

    // Account
    { id: "theme-light", label: "Set theme: light", group: "Account", perform: () => setTheme("light") },
    { id: "theme-dark", label: "Set theme: dark", group: "Account", perform: () => setTheme("dark") },
    { id: "theme-system", label: "Set theme: system", group: "Account", perform: () => setTheme("system") },
    { id: "logout", label: "Sign out", group: "Account", perform: () => logout() },
  ];

  const visible = items.filter((it) => !it.adminOnly || isSuperAdmin);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isCmd = e.metaKey || e.ctrlKey;
      if (isCmd && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      // Vim-style "g x" navigation outside of inputs.
      if (!isCmd && !e.metaKey && !e.altKey && !e.ctrlKey) {
        const tag = (document.activeElement as HTMLElement | null)?.tagName ?? "";
        if (["INPUT", "TEXTAREA", "SELECT"].includes(tag)) return;
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 pt-32"
      onClick={() => setOpen(false)}
    >
      <div className="w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
        <Command label="Command Menu" shouldFilter={true}>
          <Command.Input
            placeholder="Type a command or search…"
            value={search}
            onValueChange={setSearch}
            autoFocus
          />
          <Command.List>
            <Command.Empty>No matching commands.</Command.Empty>
            {Array.from(new Set(visible.map((v) => v.group))).map((group) => (
              <Command.Group key={group} heading={group}>
                {visible
                  .filter((it) => it.group === group)
                  .map((it) => (
                    <Command.Item
                      key={it.id}
                      value={`${it.label} ${it.id}`}
                      onSelect={() => {
                        setOpen(false);
                        it.perform();
                      }}
                    >
                      <span>{it.label}</span>
                      {it.shortcut && (
                        <kbd className="rounded-sm bg-term-elev px-1 py-0.5 text-2xs text-term-mute">
                          {it.shortcut}
                        </kbd>
                      )}
                    </Command.Item>
                  ))}
              </Command.Group>
            ))}
          </Command.List>
        </Command>
        <div className="mt-2 text-center text-2xs text-term-mute">
          <kbd className="rounded-sm bg-term-elev px-1">Esc</kbd> close ·{" "}
          <kbd className="rounded-sm bg-term-elev px-1">↑↓</kbd> nav ·{" "}
          <kbd className="rounded-sm bg-term-elev px-1">Enter</kbd> run
        </div>
      </div>
    </div>
  );
}
